import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import moment from "moment";
import { toast } from "react-toastify";

import AppLayout from "../../components/layout/AppLayout";
import Breadcrumb from "../../components/layout/Breadcrumb";
import MaterialEntryForm from "../../components/materialLines/MaterialEntryForm";
import { LoadingState } from "../../components/ui/TableStateRow";
import cx from "../../components/ui/cx";
import { cardClass, pageTitleClass } from "../../components/ui/styles";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  clearCurrentMaterialOrder,
  clearMaterialOrderPartyInfo,
  fetchMaterialOrderDetail,
  fetchMaterialOrderMaterials,
  fetchMaterialOrderParties,
  fetchMaterialOrderPartyInfo,
  getCurrentMaterialOrder,
  getCurrentMaterialOrderLoading,
  getMaterialOrderMaterials,
  getMaterialOrderMaterialsLoading,
  getMaterialOrderPartyInfo,
  getMaterialOrderPartyOptions,
} from "../../features/materialOrders/materialOrdersSlice";
import { PUT } from "../../services/axios.service";
import { emptyLine, lineKey, linePayload } from "../../static/materialLine";
import { route } from "../../static/route";
import { toastStyles } from "../../static/styles";
import { BreadcrumbType } from "../../types/common";
import { IMaterialLinesFormInput } from "../../types/materialLine";

const breadcrumbItems: BreadcrumbType[] = [
  { label: "હોમ", href: route.dashboard },
  { label: "મટીરીયલ ઓર્ડર", href: route.materialOrders },
  { label: "ઓર્ડર સુધારો" },
];

const MaterialOrderEditPage = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const currentOrder = useAppSelector(getCurrentMaterialOrder);
  const currentOrderLoading = useAppSelector(getCurrentMaterialOrderLoading);
  const partyOptions = useAppSelector(getMaterialOrderPartyOptions);
  const materials = useAppSelector(getMaterialOrderMaterials);
  const materialsLoading = useAppSelector(getMaterialOrderMaterialsLoading);
  const partyInfo = useAppSelector(getMaterialOrderPartyInfo);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const orderId = currentOrder?.id;
  const orderPartyId = currentOrder?.party_id;

  useEffect(() => {
    if (id) {
      dispatch(fetchMaterialOrderDetail(Number(id)));
      dispatch(fetchMaterialOrderMaterials());
    }
    return () => {
      dispatch(clearCurrentMaterialOrder());
      dispatch(clearMaterialOrderPartyInfo());
    };
  }, [id, dispatch]);

  // The order's own party stays in the list, and its own lines don't count as the party's last prices.
  useEffect(() => {
    if (orderId && orderPartyId) {
      dispatch(fetchMaterialOrderParties(orderPartyId));
      dispatch(
        fetchMaterialOrderPartyInfo({ party_id: orderPartyId, order_id: orderId })
      );
    }
  }, [orderId, orderPartyId, dispatch]);

  const handlePartyChange = useCallback(
    (partyId: number) => {
      if (partyId) {
        dispatch(
          fetchMaterialOrderPartyInfo({
            party_id: partyId,
            order_id: Number(id),
          })
        );
      } else {
        dispatch(clearMaterialOrderPartyInfo());
      }
    },
    [id, dispatch]
  );

  const handleSubmit = async (data: IMaterialLinesFormInput) => {
    setIsSubmitting(true);
    try {
      await PUT(`/v1/admin/material-orders/${id}`, {
        party_id: data.party_id,
        date: moment(data.date).format("YYYY-MM-DD HH:mm:ss"),
        items: linePayload(data.items, false),
      })
        .then((response: any) => {
          if (response.data.status === "success") {
            toast.success(response.data.message, {
              ...(toastStyles as any),
            });
            navigate(route.materialOrders);
          } else {
            toast.error(response.data.message, {
              ...(toastStyles as any),
            });
          }
        })
        .catch((error: any) => {
          toast.error(error.data.message, {
            ...(toastStyles as any),
          });
        });
    } catch (error) {
      toast.error("કંઈક ખોટું ગયું, કૃપા કરીને ફરીથી પ્રયત્ન કરો.", {
        ...(toastStyles as any),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const defaultValues = useMemo((): IMaterialLinesFormInput | undefined => {
    if (!currentOrder) return undefined;
    return {
      party_id: currentOrder.party_id,
      date: moment(currentOrder.date).format("YYYY-MM-DDTHH:mm"),
      items:
        currentOrder.items && currentOrder.items.length > 0
          ? currentOrder.items.map((item) => ({
              ...emptyLine,
              material_id: item.material_id,
              material_size_id: item.material_size_id,
              qty: String(item.qty),
            }))
          : [{ ...emptyLine }],
    };
  }, [currentOrder]);

  // What is received of each line: such a line stays on the order with its size.
  const received = useMemo(() => {
    const quantities: Record<string, number> = {};

    (currentOrder?.items || []).forEach((item) => {
      if ((item.received_qty || 0) > 0) {
        quantities[lineKey(item.material_id, item.material_size_id)] =
          item.received_qty || 0;
      }
    });

    return quantities;
  }, [currentOrder]);

  // The lines a purchase closed before they were received in full.
  const closed = useMemo(
    () =>
      (currentOrder?.items || [])
        .filter((item) => item.closed)
        .map((item) => lineKey(item.material_id, item.material_size_id)),
    [currentOrder]
  );

  return (
    <AppLayout>
      <div className="mb-2 md:mb-0">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      <div className="flex items-center mb-4 md:mb-6 mt-2">
        <h1 className={pageTitleClass}>મટીરીયલ ઓર્ડર સુધારો</h1>
      </div>

      <div className="flex flex-col gap-6">
        {currentOrderLoading ? (
          <div className={cx(cardClass, "p-4 text-sm")}>
            <LoadingState />
          </div>
        ) : currentOrder && defaultValues ? (
          <MaterialEntryForm
            key={currentOrder.id}
            defaultValues={defaultValues}
            partyOptions={partyOptions}
            materials={materials}
            materialsLoading={materialsLoading}
            partyInfo={partyInfo}
            onPartyChange={handlePartyChange}
            partyLocked={(currentOrder.purchases || []).length > 0}
            received={received}
            closed={closed}
            onSubmit={handleSubmit}
            isSubmitting={isSubmitting}
            onCancel={() => navigate(route.materialOrders)}
            submitLabel="સાચવો"
          />
        ) : (
          <div className={cx(cardClass, "p-8 text-center text-gray-500")}>
            મટીરીયલ ઓર્ડર ની વિગતો મળી નથી
          </div>
        )}
      </div>
    </AppLayout>
  );
};
export default MaterialOrderEditPage;
